import { useEffect, useMemo, useState } from "react";
import { Loader2, Mail, Phone, Trash2, Eye, EyeOff, Archive, FileText, ExternalLink } from "lucide-react";
import api, { formatApiErrorDetail } from "./adminApi";
import { toast } from "sonner";

const STATUS_LABEL = {
  new: { text: "Nova", cls: "bg-gold/20 text-gold border-gold/40" },
  read: { text: "Lida", cls: "bg-white/10 text-white/70 border-white/20" },
  archived: { text: "Arquivada", cls: "bg-white/5 text-white/40 border-white/10" },
};

const fileUrl = (u) => (u?.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);

export default function AdminApplications() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/applications");
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    if (!items) return null;
    if (filter === "all") return items;
    return items.filter((a) => (a.status || "new") === filter);
  }, [items, filter]);

  const counts = useMemo(() => {
    if (!items) return { all: 0, new: 0, read: 0, archived: 0 };
    return items.reduce(
      (acc, a) => {
        const s = a.status || "new";
        acc.all += 1;
        acc[s] = (acc[s] || 0) + 1;
        return acc;
      },
      { all: 0, new: 0, read: 0, archived: 0 },
    );
  }, [items]);

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/applications/${id}`, { status });
      setItems((cur) => (cur ? cur.map((a) => (a.id === id ? { ...a, status } : a)) : cur));
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Apagar esta candidatura e os ficheiros enviados?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/applications/${id}`);
      setItems((cur) => (cur ? cur.filter((a) => a.id !== id) : cur));
      toast.success("Candidatura apagada.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  if (items === null) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div data-testid="admin-applications-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl text-white">Candidaturas</h1>
          <p className="mt-1 text-sm text-white/50">Recebidas via formulário público de /candidaturas.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {["all", "new", "read", "archived"].map((k) => (
            <button
              key={k}
              data-testid={`applications-filter-${k}`}
              onClick={() => setFilter(k)}
              className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-widest transition ${
                filter === k ? "bg-gold text-black" : "border border-white/15 text-white/60 hover:text-white"
              }`}
            >
              {k === "all" ? "Todas" : STATUS_LABEL[k].text} · {counts[k]}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-12 text-center">
          <p className="text-sm text-white/50">Sem candidaturas nesta vista.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((a) => (
            <ApplicationCard
              key={a.id}
              app={a}
              busy={busyId === a.id}
              onMark={(s) => setStatus(a.id, s)}
              onDelete={() => remove(a.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const ApplicationCard = ({ app, busy, onMark, onDelete }) => {
  const status = app.status || "new";
  const badge = STATUS_LABEL[status] || STATUS_LABEL.new;
  const when = app.created_at ? new Date(app.created_at).toLocaleString("pt-PT") : "";

  return (
    <div
      data-testid={`application-${app.id}`}
      className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 sm:p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-xl text-white">{app.name}</h3>
            <span className={`rounded-full border px-2.5 py-0.5 text-[10px] uppercase tracking-widest ${badge.cls}`}>
              {badge.text}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-white/70">
            <a href={`mailto:${app.email}`} className="flex items-center gap-1.5 hover:text-gold">
              <Mail className="h-3.5 w-3.5" strokeWidth={1.5} /> {app.email}
            </a>
            <a href={`tel:${app.phone}`} className="flex items-center gap-1.5 hover:text-gold">
              <Phone className="h-3.5 w-3.5" strokeWidth={1.5} /> {app.phone}
            </a>
            <span className="text-white/40">{when}</span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {status !== "read" && (
            <button
              disabled={busy}
              data-testid={`application-mark-read-${app.id}`}
              onClick={() => onMark("read")}
              className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[11px] uppercase tracking-widest text-white/70 transition hover:text-white disabled:opacity-50"
            >
              <Eye className="h-3.5 w-3.5" strokeWidth={1.5} /> Marcar lida
            </button>
          )}
          {status !== "new" && (
            <button
              disabled={busy}
              onClick={() => onMark("new")}
              className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[11px] uppercase tracking-widest text-white/70 transition hover:text-white disabled:opacity-50"
            >
              <EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} /> Marcar nova
            </button>
          )}
          {status !== "archived" && (
            <button
              disabled={busy}
              onClick={() => onMark("archived")}
              className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[11px] uppercase tracking-widest text-white/70 transition hover:text-white disabled:opacity-50"
            >
              <Archive className="h-3.5 w-3.5" strokeWidth={1.5} /> Arquivar
            </button>
          )}
          <button
            disabled={busy}
            data-testid={`application-delete-${app.id}`}
            onClick={onDelete}
            className="flex items-center gap-1.5 rounded-full border border-red-400/30 px-3 py-1.5 text-[11px] uppercase tracking-widest text-red-300/80 transition hover:bg-red-500/10 hover:text-red-200 disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} /> Apagar
          </button>
        </div>
      </div>

      {app.message && (
        <p className="mt-4 whitespace-pre-wrap rounded-lg border border-white/5 bg-black/30 p-4 text-sm leading-relaxed text-white/80">
          {app.message}
        </p>
      )}

      {(app.files || []).length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-[11px] uppercase tracking-widest text-white/40">Ficheiros ({app.files.length})</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-6">
            {app.files.map((f, i) => {
              const url = fileUrl(f.url);
              const isPdf = (f.content_type || "").includes("pdf") || (f.original_filename || "").toLowerCase().endsWith(".pdf");
              return (
                <a
                  key={i}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`application-file-${app.id}-${i}`}
                  className="group relative block overflow-hidden rounded-lg border border-white/10 bg-black/40"
                  title={f.original_filename || `Ficheiro ${i + 1}`}
                >
                  {isPdf ? (
                    <div className="flex aspect-square flex-col items-center justify-center gap-2 p-3 text-center">
                      <FileText className="h-8 w-8 text-gold" strokeWidth={1.5} />
                      <span className="truncate text-[10px] text-white/60">{f.original_filename || "PDF"}</span>
                    </div>
                  ) : (
                    <img
                      src={url}
                      alt={f.original_filename || `Foto ${i + 1}`}
                      loading="lazy"
                      className="aspect-square w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  )}
                  <span className="absolute right-1.5 top-1.5 rounded-full bg-black/70 p-1 opacity-0 transition group-hover:opacity-100">
                    <ExternalLink className="h-3 w-3 text-white" strokeWidth={1.5} />
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
